import { tripPeriod } from '../../lib/format'

/**
 * Der Zeitraum eines Buches — zwei Tage, beide freiwillig.
 *
 * **Warum eine eigene Datei.** Die Regel stand als `useMemo` mitten in
 * `BuchPage` und galt damit nur dort. Das private Dokument kannte sie nicht,
 * und die Infoseite, die den Zeitraum jetzt anbietet, hätte sie ein drittes
 * Mal gebraucht. Drei Kopien derselben Rechnung laufen auseinander — in diesem
 * Haus ist das schon dreimal passiert und steht deshalb in `CLAUDE.md`.
 *
 * **Gerechnet wird in der Uhr der Reise**, nicht in der des Geräts. Wer „ab
 * 24. August" wählt, meint den 24. August dort, wo die Reise stattfand. In der
 * Datenbank steht UTC; ein Beitrag um 01:00 Uhr Ortszeit in UTC+2 liegt dort
 * am Vortag um 23:00 und wäre sonst aus seinem eigenen Tag herausgefallen.
 * Dieselbe Verschiebung wie in `buchTag()`, damit Trenner und Grenze denselben
 * Tag meinen.
 */
export type Zeitraum = { von: string | null; bis: string | null }

const MINUTE = 60_000

/** Was in der Adresse steht — leer und fehlend sind dasselbe. */
export function zeitraumAus(suche: URLSearchParams): Zeitraum {
  const wert = (name: string): string | null => {
    const roh = suche.get(name)
    return roh === null || roh === '' ? null : roh
  }
  return { von: wert('von'), bis: wert('bis') }
}

/** Ob überhaupt zugeschnitten wird. */
export function istGesetzt(zeitraum: Zeitraum): boolean {
  return zeitraum.von !== null || zeitraum.bis !== null
}

/**
 * Die Prüfung selbst.
 *
 * **Ohne Zeitpunkt gilt: nur dann dabei, wenn gar nicht zugeschnitten wird.**
 * Ein Punkt ohne Datum lässt sich keinem Zeitraum zuordnen; ihn trotzdem
 * mitzudrucken hieße, einen Ausschnitt zu behaupten, den es nicht gibt.
 */
export function imZeitraum(
  zeitraum: Zeitraum,
  versatz: number | null,
): (iso: string | null) => boolean {
  const schiebe = (versatz ?? 0) * MINUTE
  const ab = zeitraum.von === null ? null : Date.parse(`${zeitraum.von}T00:00:00Z`) - schiebe
  const zu = zeitraum.bis === null ? null : Date.parse(`${zeitraum.bis}T23:59:59.999Z`) - schiebe

  return (iso) => {
    if (iso === null) return ab === null && zu === null
    const wann = new Date(iso).getTime()
    if (Number.isNaN(wann)) return false
    if (ab !== null && wann < ab) return false
    if (zu !== null && wann > zu) return false
    return true
  }
}

/**
 * Der Zeitraum an eine Adresse gehängt — **an einer Stelle.**
 *
 * Die Infoseite verweist auf das Buch und auf jedes private Dokument; jede
 * dieser Adressen von Hand zusammenzusetzen wäre dieselbe Zeile so oft, wie es
 * Mitlesende gibt.
 */
export function mitZeitraum(basis: string, zeitraum: Zeitraum): string {
  const felder = new URLSearchParams()
  if (zeitraum.von !== null) felder.set('von', zeitraum.von)
  if (zeitraum.bis !== null) felder.set('bis', zeitraum.bis)
  const text = felder.toString()
  return text === '' ? basis : `${basis}?${text}`
}

/**
 * „24. – 30. August 2026" — für das Deckblatt.
 *
 * **Ein Buch, das nur einen Teil der Reise enthält, sagt das auf der ersten
 * Seite.** Sonst hält es jemand in ein paar Jahren für den ganzen Bericht und
 * sucht vergeblich nach dem Rest. `tripPeriod()` kann die Fälle schon, in
 * denen nur eine der beiden Grenzen feststeht („ab …", „bis …").
 */
export function zeitraumText(zeitraum: Zeitraum): string {
  return tripPeriod(zeitraum.von, zeitraum.bis)
}
